import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import dayjs from "dayjs";
import { submitMessageFeedbackApi } from "../../api/chatbot";

// Lightweight inline markdown: turns **bold** into <strong>, leaves the rest as plain text
function renderInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
}

// Lightweight markdown: renders "- item" lines as bullet lists and **bold** inline,
// since the LLM responds in markdown but the chat bubble otherwise shows plain text.
function renderMarkdown(content) {
  if (!content) return content;
  const lines = content.split("\n");
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length) {
      blocks.push(
        <ul key={`list-${blocks.length}`} style={{ margin: "4px 0", paddingLeft: "20px" }}>
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item, `li-${blocks.length}-${i}`)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    const bulletMatch = trimmed.match(/^[-*]\s+(.*)/);
    if (bulletMatch) {
      listItems.push(bulletMatch[1]);
    } else {
      flushList();
      if (trimmed === "") {
        blocks.push(<div key={`gap-${idx}`} style={{ height: "8px" }} />);
      } else {
        blocks.push(<div key={`line-${idx}`}>{renderInline(line, `line-${idx}`)}</div>);
      }
    }
  });
  flushList();

  return blocks;
}

function FeedbackButtons({ messageId, initialFeedback, initialNote }) {
  const [feedback,    setFeedback]    = useState(initialFeedback ?? null);
  const [hovered,     setHovered]     = useState(false);
  const [pendingVal,  setPendingVal]  = useState(null);   // which button was clicked, awaiting note
  const [note,        setNote]        = useState(initialNote || "");
  const [saving,      setSaving]      = useState(false);
  const [submitted,   setSubmitted]   = useState(initialFeedback !== null && initialFeedback !== undefined);

  const handleClick = (value) => {
    if (saving) return;
    if (submitted && feedback === value) return;
    setPendingVal(value);
    setNote("");
  };

  const submitFeedback = async () => {
    if (saving || pendingVal === null) return;
    setSaving(true);
    try {
      await submitMessageFeedbackApi(messageId, pendingVal, note.trim());
      setFeedback(pendingVal);
      setSubmitted(true);
      setPendingVal(null);
    } catch (_) {
      // silently ignore — user can retry
    } finally {
      setSaving(false);
    }
  };

  const cancelNote = () => {
    setPendingVal(null);
    setNote("");
  };

  const visible = hovered || feedback !== null;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!pendingVal) setHovered(false); }}
      style={{ marginTop: "6px" }}
    >
      {/* Thumbs row */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "4px", minHeight: "22px" }}>
        {visible && (
          <>
            <Tooltip title="Helpful" placement="bottom" arrow>
              <button
                onClick={() => handleClick(1)}
                disabled={saving}
                style={{
                  background:   feedback === 1 ? "#e8f5e9" : "transparent",
                  border:       feedback === 1 ? "1px solid #81c784" : "1px solid transparent",
                  borderRadius: "4px",
                  cursor:       "pointer",
                  padding:      "1px 5px",
                  fontSize:     "14px",
                  lineHeight:   "1",
                  opacity:      saving ? 0.5 : 1,
                }}
              >
                👍
              </button>
            </Tooltip>
            <Tooltip title="Not helpful" placement="bottom" arrow>
              <button
                onClick={() => handleClick(-1)}
                disabled={saving}
                style={{
                  background:   feedback === -1 ? "#ffebee" : "transparent",
                  border:       feedback === -1 ? "1px solid #ef9a9a" : "1px solid transparent",
                  borderRadius: "4px",
                  cursor:       "pointer",
                  padding:      "1px 5px",
                  fontSize:     "14px",
                  lineHeight:   "1",
                  opacity:      saving ? 0.5 : 1,
                }}
              >
                👎
              </button>
            </Tooltip>
          </>
        )}
      </div>

      {/* Note input — shown after clicking either button */}
      {pendingVal !== null && (
        <div
          style={{
            marginTop:    "8px",
            padding:      "10px 12px",
            background:   "#f9f9f9",
            border:       "1px solid #e0e0e0",
            borderRadius: "8px",
            display:      "flex",
            flexDirection:"column",
            gap:          "8px",
          }}
        >
          <span style={{ fontSize: "12px", color: "#666" }}>
            {pendingVal === -1 ? "What went wrong? (optional)" : "What did you like? (optional)"}
          </span>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note…"
            maxLength={500}
            rows={2}
            style={{
              width:        "100%",
              resize:       "none",
              border:       "1px solid #d0d0d0",
              borderRadius: "6px",
              padding:      "6px 8px",
              fontSize:     "13px",
              fontFamily:   "inherit",
              outline:      "none",
              boxSizing:    "border-box",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitFeedback(); }
              if (e.key === "Escape") cancelNote();
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <button
              onClick={cancelNote}
              style={{
                background: "transparent", border: "1px solid #ccc",
                borderRadius: "6px", padding: "4px 12px",
                fontSize: "12px", cursor: "pointer", color: "#555",
              }}
            >
              Cancel
            </button>
            <button
              onClick={submitFeedback}
              disabled={saving}
              style={{
                background:   pendingVal === -1 ? "#ffebee" : "#e8f5e9",
                border:       pendingVal === -1 ? "1px solid #ef9a9a" : "1px solid #81c784",
                borderRadius: "6px", padding: "4px 12px",
                fontSize: "12px", cursor: "pointer",
                color: pendingVal === -1 ? "#c62828" : "#2e7d32",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// A file/image the user uploaded mid-conversation -- rendered from a real
// persisted ChatMessage, so it shows the same way live and when a session is
// reopened later.
function AttachmentBubble({ content, created_on }) {
  let ref;
  try {
    ref = JSON.parse(content);
  } catch (_) {
    return null;
  }
  const { url, file_name, content_type } = ref;
  const isImage = (content_type || "").startsWith("image/");
  const time = created_on ? dayjs(created_on).format("h:mm A") : "";

  return (
    <div className="chat-bubble-wrapper chat-bubble-wrapper--user">
      <Tooltip title={time} placement="left" arrow>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <div
            className="chat-bubble chat-bubble--user"
            style={{
              padding: isImage ? 4 : undefined,
              display: "flex",
              alignItems: "center",
              gap: 8,
              maxWidth: 220,
            }}
          >
            {isImage ? (
              <img
                src={url}
                alt={file_name}
                style={{ maxWidth: 200, maxHeight: 160, borderRadius: 8, display: "block" }}
              />
            ) : (
              <>
                <InsertDriveFileIcon fontSize="small" />
                <span style={{ fontSize: 13, wordBreak: "break-all" }}>{file_name}</span>
              </>
            )}
          </div>
        </a>
      </Tooltip>
    </div>
  );
}

function MessageBubble({ message }) {
  const { id, role, content, created_on, streaming, feedback } = message;

  if (role === "attachment") {
    return <AttachmentBubble content={content} created_on={created_on} />;
  }

  const isUser      = role === "user";
  const isAssistant = role === "assistant";
  const time        = created_on ? dayjs(created_on).format("h:mm A") : "";

  // Show typing dots while waiting for first token or if LLM is building a JSON action response
  const isThinking = streaming && (!content || content.trimStart().startsWith("{"));

  return (
    <div className={`chat-bubble-wrapper chat-bubble-wrapper--${role}`}>
      <Tooltip title={time} placement={isUser ? "left" : "right"} arrow>
        <div
          className={`chat-bubble chat-bubble--${role}`}
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {isThinking ? (
            <div className="typing-indicator">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <>
              {renderMarkdown(content)}
              {isAssistant && !streaming && id && (
                <FeedbackButtons messageId={id} initialFeedback={feedback ?? null} initialNote={message.feedback_note || ""} />
              )}
            </>
          )}
        </div>
      </Tooltip>
    </div>
  );
}

export default MessageBubble;
